import { useCallback, useEffect, useState } from 'react';
import useQueryParams from '../../../hooks/useQueryParams';
import alertService from '../../../services/AlertService';
import windowService from '../../../services/windowService';
import { logger } from '../../../utils/logger';
import { useUser } from '../../authentication';
import { createOrgAccount } from '../../org';
import { getAssociatedChurchAccounts } from '../api/getAssociatedChurchAccounts';
import { ChurchAccount } from '../models/churchAccount';
import { redirectToCreateChurchAccount } from '../utils/redirectToCreateChurchAccount';
import { sortAssociatedChurchAccounts } from '../utils/sortAssociatedChurchAccounts';

type ChurchAccountCreationStatus = 'loading' | 'error' | 'submitting' | null;

type UseChurchAccountCreationResult = {
  status: ChurchAccountCreationStatus;
  churchAccounts: ChurchAccount[];
  selectedChurchAccountId?: string;
  onSelectChurchAccount: (churchAccount: ChurchAccount) => void;
  onCreateChurch: () => void;
  onCreateChurchAccount: () => void;
  onBack: () => void;
};

export const useChurchAccountCreation = (): UseChurchAccountCreationResult => {
  const { user } = useUser();
  const { redirect } = useQueryParams();
  const [status, setStatus] = useState<ChurchAccountCreationStatus>('loading');
  const [churchAccounts, setChurchAccounts] = useState<ChurchAccount[]>([]);
  const [selectedChurchAccount, setSelectedChurchAccount] = useState<ChurchAccount>();

  const onCreateChurchAccount = useCallback(() => {
    redirectToCreateChurchAccount({
      action: user?.lastSelectedAccount ? 'link' : 'create',
      redirectPath: redirect || '/org',
    });
  }, [redirect, user?.lastSelectedAccount]);

  const onBack = () => windowService.redirectBack();

  const onSelectChurchAccount = (churchAccount: ChurchAccount) => {
    if (churchAccount.orgId) return;
    setSelectedChurchAccount(churchAccount);
  };

  const onCreateChurch = () => {
    if (!selectedChurchAccount || status === 'submitting') return;

    setStatus('submitting');

    createOrgAccount(
      selectedChurchAccount.churchAccountId,
      selectedChurchAccount.name,
      user?.userId || '',
      selectedChurchAccount.churchAccountId
    )
      .then(() => {
        alertService.showOnNextPage('Church Created');
        windowService.redirectTo(redirect || '/org');
      })
      .catch(error => {
        logger.error('Error creating church account', { error });
        alertService.showError();
        setStatus(null);
      });
  };

  useEffect(() => {
    getAssociatedChurchAccounts()
      .then(lifewayChurchAccounts => {
        if (!lifewayChurchAccounts.length) {
          onCreateChurchAccount();
          return;
        }

        const sortedAssociatedChurchAccounts = sortAssociatedChurchAccounts(lifewayChurchAccounts);

        setChurchAccounts(sortedAssociatedChurchAccounts);
        const firstSelectableChurchAccount = sortedAssociatedChurchAccounts.find(account => !account.orgId);
        setSelectedChurchAccount(firstSelectableChurchAccount);
        setStatus(null);
      })
      .catch(error => {
        logger.error('Error fetching associated church accounts', error);
        setStatus('error');
      });
  }, []);

  return {
    status,
    churchAccounts,
    selectedChurchAccountId: selectedChurchAccount?.churchAccountId,
    onSelectChurchAccount,
    onCreateChurch,
    onCreateChurchAccount,
    onBack,
  };
};
